import React from 'react';
import { SystemConfig } from '@/types';
import { Mail, Plus, Smartphone, Trash2 } from 'lucide-react';
import { validateEmail } from '@/lib/validators';

interface CommunicationsSectionProps {
  config: SystemConfig;
  onUpdate: (config: SystemConfig) => void;
  onSetMessage: (msg: { type: 'success' | 'error'; text: string } | null) => void;
  invalidBookingAuditIndexes: Set<number>;
}

export const CommunicationsSection: React.FC<CommunicationsSectionProps> = ({
  config,
  onUpdate,
  onSetMessage,
  invalidBookingAuditIndexes
}) => {
  const bookingAuditEmails = config.templates.notificationSettings?.bookingAuditEmails || [];

  const setBookingAuditEmails = (emails: string[]) => {
    onUpdate({
      ...config,
      templates: {
        ...config.templates,
        notificationSettings: {
          ...config.templates.notificationSettings,
          bookingAuditEmails: emails
        }
      }
    });
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
        <h4 className="font-bold text-lg mb-6 text-slate-800 flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" /> SMTP Gateway
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-2">Host</label>
            <input
              className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
              value={config.smtp.host}
              onChange={(e) =>
                onUpdate({ ...config, smtp: { ...config.smtp, host: e.target.value } })
              }
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-2">Port</label>
            <input
              type="number"
              className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
              value={config.smtp.port}
              onChange={(e) =>
                onUpdate({ ...config, smtp: { ...config.smtp, port: Number(e.target.value) } })
              }
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-2">User / Email</label>
            <input
              className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
              value={config.smtp.user}
              onChange={(e) =>
                onUpdate({ ...config, smtp: { ...config.smtp, user: e.target.value } })
              }
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-2">Password</label>
            <input
              type="password"
              className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
              value={config.smtp.pass}
              onChange={(e) =>
                onUpdate({ ...config, smtp: { ...config.smtp, pass: e.target.value } })
              }
            />
          </div>
        </div>
      </div>

      <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
        <h4 className="font-bold text-lg mb-6 text-slate-800 flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-green-600" /> Twilio SMS Gateway
        </h4>
        <div className="space-y-6">
          <label className="flex items-center gap-3 cursor-pointer p-4 border rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
            <input
              type="checkbox"
              checked={config.twilio.enabled}
              onChange={(e) =>
                onUpdate({ ...config, twilio: { ...config.twilio, enabled: e.target.checked } })
              }
              className="w-5 h-5 text-teal-600 rounded"
            />
            <div>
              <span className="font-bold text-slate-700 block">Enable Automated SMS</span>
              <span className="text-[10px] text-slate-500 font-medium uppercase tracking-tight">
                Requires valid Twilio credentials below
              </span>
            </div>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-2">Account SID</label>
              <input
                className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
                value={config.twilio.accountSid}
                onChange={(e) =>
                  onUpdate({ ...config, twilio: { ...config.twilio, accountSid: e.target.value } })
                }
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-2">Auth Token</label>
              <input
                type="password"
                className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
                value={config.twilio.authToken}
                onChange={(e) =>
                  onUpdate({ ...config, twilio: { ...config.twilio, authToken: e.target.value } })
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
        <h4 className="font-bold text-lg mb-3 text-slate-800 flex items-center gap-2">
          <Mail className="w-5 h-5 text-teal-600" /> Booking Audit Email Recipients
        </h4>
        <p className="text-xs text-slate-500 mb-4">
          New booking confirmations will also be emailed to these addresses.
        </p>
        <div className="space-y-3">
          {(bookingAuditEmails.length > 0 ? bookingAuditEmails : ['']).map((email, index) => (
            <div key={`booking-audit-${index}`} className="flex items-center gap-2">
              <input
                type="email"
                placeholder="sammy@k2a.uk.com"
                className={`flex-1 border rounded-xl p-3 bg-slate-50 font-mono text-sm outline-none focus:ring-2 ${invalidBookingAuditIndexes.has(index) ? 'border-red-300 focus:ring-red-500' : 'focus:ring-teal-500'}`}
                value={email}
                onChange={(e) => {
                  const next = [...bookingAuditEmails];
                  next[index] = e.target.value.trim().toLowerCase();
                  setBookingAuditEmails(next);
                }}
              />
              <button
                type="button"
                onClick={() =>
                  setBookingAuditEmails(bookingAuditEmails.filter((_, i) => i !== index))
                }
                className="p-2.5 rounded-lg border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-all"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const hasEmpty = bookingAuditEmails.some((e) => !e.trim());
              const hasInvalid = bookingAuditEmails.some(
                (e) => e.trim() && !validateEmail(e).valid
              );
              if (hasEmpty || hasInvalid) {
                onSetMessage({
                  type: 'error',
                  text: 'Fix invalid email rows before adding another recipient.'
                });
                return;
              }
              setBookingAuditEmails([...bookingAuditEmails, '']);
            }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-teal-200 bg-teal-50 text-teal-700 text-xs font-bold hover:bg-teal-100 transition-all"
          >
            <Plus className="w-4 h-4" /> Add Recipient
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-2">Add one recipient per row.</p>
        {invalidBookingAuditIndexes.size > 0 && (
          <p className="text-[10px] text-red-500 mt-1">One or more email addresses are invalid.</p>
        )}
      </div>
    </div>
  );
};
